import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { useGlobalState } from './GlobalStateContext';
import { API_BASE_URL } from '../config';

export interface Project {
  id: string;
  name: string;
  description?: string;
  gameSummary?: string;
  artworkUrl?: string;
  links?: { url: string; label?: string; category?: number }[];
  hasArtwork?: boolean;
  websiteUrl?: string;
  steamUrl?: string;
  itchioUrl?: string;
  switchUrl?: string;
  playstationUrl?: string;
  tags?: string[];
  role?: string;
  experience: number;
  levelInfo?: {
    level: number;
    currentXp: number;
    nextLevelXp: number;
    progressPercent: number;
    badgeColor: string;
  };
  streakCount: number;
  lastPostAt?: string;
  userGames?: any[];
  socialAccounts?: any[];
  updatedAt?: string;
  createdAt: string;
  archived: boolean;
  youtubeFallbackEnabled?: boolean;
  youtubeFallbackAccountId?: string;
}

interface ProjectContextType {
  projects: Project[];
  activeProject: Project | null;
  setActiveProject: (project: Project | null) => void;
  refreshProjects: () => Promise<void>;
  loading: boolean;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [activeProject, setActiveProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const { user, setIsServerOffline } = useGlobalState();
  const queryClient = useQueryClient();

  const refreshProjects = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setProjects([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/games`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newProjects = response.data;
      setProjects(newProjects);
      setIsServerOffline(false);

      // Invalidate React Query cache for games
      queryClient.invalidateQueries({ queryKey: ['games'] });

      // Update active project if it exists in the new list to get fresh data (like social accounts)
      setActiveProject(current => {
        if (current) {
          const updatedActive = newProjects.find((p: Project) => p.id === current.id);
          return updatedActive || current;
        } else if (newProjects.length > 0) {
          // Set active project if not set
          const lastProjectId = localStorage.getItem('lastProjectId');
          const lastProject = newProjects.find((p: Project) => p.id === lastProjectId);
          return lastProject || newProjects[0];
        }
        return null;
      });
    } catch (err: any) {
      console.error('Error fetching projects:', err);
      if (!err.response) {
        setIsServerOffline(true);
      }
    } finally {
      setLoading(false);
    }
  }, [setIsServerOffline]);

  useEffect(() => {
    if (localStorage.getItem('token')) {
      setLoading(true);
    }
    refreshProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (activeProject) {
      localStorage.setItem('lastProjectId', activeProject.id);
    }
  }, [activeProject]);

  return (
    <ProjectContext.Provider value={{ projects, activeProject, setActiveProject, refreshProjects, loading }}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProjects = () => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProjects must be used within a ProjectProvider');
  }
  return context;
};

